'use client';

import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Divider } from "@mui/material";
import IntroAnchor from "@/app/features/shared/__to_be_integrated/ui/IntroAnchor";
import Grid from "@mui/material/Grid2";
import clsx from "clsx";
import sharedStyles from "@/app/features/sites/guided-tours/ui/shared/guided-tours-shared.module.css";
import publicStyles from "@/app/features/sites/guided-tours/ui/public/guided-tours-public.module.css";

export function ExhibitionsOverviewIntroFrame({ children }: { children: ReactNode }) {
    return (
        <span className="relative">
            {children}
        </span>
    );
}

export function ExhibitionsOverviewHeaderFrame({ children }: { children: ReactNode }) {
    return (
        <>
            <IntroAnchor />
            <Divider
                sx={{
                    pt: 4,
                    width: 'min(90%, var(--content-max-width))',
                }}
            >
                {children}
            </Divider>
        </>
    );
}

export function ExhibitionsOverviewTilesFrame({ children }: { children: ReactNode }) {
    return (
        <Box
            className="flex flex-auto min-w-0 min-h-0 w-full justify-center"
            sx={{ py: 4, maxWidth: "var(--content-max-width)" }}
        >
            <Grid className="flex flex-auto px-[5%] md:px-[10%]" spacing={2} container columns={{ xs: 1, md: 2 }}>
                {children}
            </Grid>
        </Box>
    );
}

export function ExhibitionsOverviewTileImageFrame({ children }: { children: ReactNode }) {
    return (
        <div className={publicStyles.tileImageFrame}>
            {children}
        </div>
    );
}

export function ExhibitionsOverviewTileCardFrame({
    children,
    revealOnView = false,
    animated = true,
    revealDelay = "0s",
    elevated = false,
    className,
}: {
    children: ReactNode;
    revealOnView?: boolean;
    animated?: boolean;
    revealDelay?: string;
    elevated?: boolean;
    className?: string;
}) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(!revealOnView);

    useEffect(() => {
        if (!revealOnView || isVisible || !ref.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) return;
                setIsVisible(true);
                observer.disconnect();
            },
            {
                threshold: 0.1,
                rootMargin: "0px 0px -5% 0px",
            }
        );

        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [isVisible, revealOnView]);

    return (
        <div
            ref={ref}
            className={clsx(
                sharedStyles.tileCard,
                elevated && sharedStyles.tileCardElevated,
                !animated && sharedStyles.tileCardStatic,
                animated && isVisible && sharedStyles.tileCardVisible,
                className
            )}
            style={{ "--tile-delay": revealDelay } as CSSProperties}
        >
            {children}
        </div>
    );
}
